/**
 * Inspector mount for routes that skip client hydration (homepage, careers pages —
 * see LayoutDefault.tsx and StaticPageLayout.tsx). These routes still run the full
 * React tree through Vike's SSR renderer (just never call hydrateRoot), so a Context
 * Provider wrapping them server-side takes effect in the emitted HTML — markers from
 * `<Msg>` get baked directly into the static HTML with no client JS required for the
 * visual outlining. Only usage recording and Alt+Click-to-copy need a small inline
 * script, since those normally run from a `useEffect` that never fires here.
 *
 * `enabled` is computed server-side in vike-handler.ts (`canReviewTranslations`) and
 * is never available to flip client-side on these routes, so there is no working
 * toggle button here (unlike InspectorOverlay.tsx) — turning the inspector on/off
 * happens once, from the backoffice testing page, and is picked up on next load.
 *
 * Off-by-default / zero-cost: renders children untouched when `enabled` is false.
 */
import React from 'react';
import { OVERLAY_STYLE } from '../client/InspectorOverlay';
import { StaticInspectorProvider } from '../client/inspector';
import { buildStaticInspectorScript } from './build-static-inspector-script';

export function StaticInspectorMount({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  if (!enabled) return <>{children}</>;

  return (
    <StaticInspectorProvider enabled>
      {children}
      <style>{OVERLAY_STYLE}</style>
      <div
        style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2147483000 }}
        className="rounded-lg bg-jc-elevated shadow-lg ring-1 ring-jc-border p-2 text-xs"
      >
        <div className="rounded px-2 py-1 font-semibold bg-blue-600 text-white">
          Inspector: ON (static route)
        </div>
        <div className="mt-1 text-jc-ink-secondary">
          Hover a dashed string to see its id · Alt+Click to copy id · Shift+Alt+Click to copy text.
          Toggle off from the backoffice testing page.
        </div>
      </div>
      {/* Must come after the marked-up content above — inline scripts run synchronously
          as the HTML streams, same ordering requirement as ResourceCookieConsentBanner. */}
      <script dangerouslySetInnerHTML={{ __html: buildStaticInspectorScript() }} />
    </StaticInspectorProvider>
  );
}
